import { ArrowLeft, BarChart2, Users, BookOpen } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ROUTES, getMasterclassStatsRoute } from '@/config/routes';
import { useMasterclass } from '@/contexts/MasterclassContext';
import { useTranslations } from '@/hooks/use-translations';
import { useCasesForMasterclass } from '@/modules/case-selector/hooks/useCasesForMasterclass';

/**
 * Masterclass Stats Overview Page
 *
 * Displays a list of patient cases in the masterclass.
 * Users can click each case to view detailed statistics for that case.
 */
export default function MasterclassStatsOverview() {
  const { t } = useTranslations('common');
  const { masterclassId } = useParams<{ masterclassId: string }>();
  const navigate = useNavigate();

  // Fetch masterclass with its patient cases
  const { cases, masterclassTitle, loading } = useCasesForMasterclass(masterclassId);

  // Set masterclass context for breadcrumbs
  const { setSelectedMasterclass, setMasterclassTitle } = useMasterclass();

  useEffect(() => {
    if (masterclassId) {
      setSelectedMasterclass(masterclassId);
    }
    if (masterclassTitle) {
      setMasterclassTitle(masterclassTitle);
    }
    return () => {
      setSelectedMasterclass(null);
      setMasterclassTitle(null);
    };
  }, [masterclassId, masterclassTitle, setSelectedMasterclass, setMasterclassTitle]);

  const handleCaseClick = (caseId: string) => {
    if (masterclassId) {
      navigate(getMasterclassStatsRoute(masterclassId, caseId));
    }
  };

  if (loading) {
    return (
      <div className="h-full w-full overflow-auto">
        <div className="container mx-auto px-4 py-4 md:py-10">
          <Skeleton className="mb-4 h-10 w-32" />
          <Skeleton className="mb-2 h-8 w-64" />
          <Skeleton className="mb-8 h-5 w-48" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto">
      <div className="container mx-auto px-4 py-4 md:py-10">
        {/* Back Button */}
        <Button
          variant="outline"
          className="mb-4 border-livelinx-lightgray100 bg-white text-livelinx-purple200 transition-all duration-200 hover:border-livelinx-purple100 hover:bg-livelinx-purple50 hover:text-livelinx-purple200"
          onClick={() => navigate(ROUTES.MASTERCLASS_HOME)}
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          {t('buttons.back')}
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 font-heading text-2xl text-livelinx-purple200 md:text-4xl">
            {t('stats.title')}
          </h1>
          <p className="text-lg text-livelinx-grey200">{masterclassTitle || masterclassId}</p>
        </div>

        {/* Summary Card */}
        <Card className="mb-8 shadow-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-livelinx-purple50">
                <BookOpen className="h-6 w-6 text-livelinx-purple200" />
              </div>
              <div>
                <p className="text-sm text-livelinx-grey200">{t('stats.patientCases')}</p>
                <p className="font-heading text-3xl text-livelinx-purple200">{cases.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Patient Cases Grid */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-card-foreground">{t('stats.selectCase')}</h2>
          <p className="text-sm text-muted-foreground">{t('stats.selectCaseDescription')}</p>
        </div>

        {cases.length === 0 ? (
          <Card className="shadow-white">
            <CardContent className="py-12 text-center">
              <BookOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
              <p className="text-muted-foreground">{t('stats.noCases')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {cases.map((caseItem, index) => (
              <Card
                key={caseItem.id}
                className="cursor-pointer shadow-white transition-all hover:border-livelinx-purple100 hover:shadow-blue-shadow"
                onClick={() => handleCaseClick(caseItem.id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-livelinx-purple50">
                      <span className="font-heading text-lg text-livelinx-purple200">
                        {index + 1}
                      </span>
                    </div>
                    <BarChart2 className="h-5 w-5 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <CardTitle className="mb-2 text-lg font-semibold text-card-foreground">
                    {caseItem.name}
                  </CardTitle>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{t('stats.viewUserStats')}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
